import { UseVoucherInterface, Voucher, ParamListVoucher, ParamAppliedCoupon, ParamVoucherInfo } from "./useVoucher";
import { AppliedCoupon } from "~/modules/GraphQL/types";
import { getDataAxios, postDataAxios } from "~/helpers";
import api from "~/config/extensionsApi";
import { ref, useContext } from "@nuxtjs/composition-api";
import { useClevertapStore } from "~/stores/clevertap";

export function useVoucher(): UseVoucherInterface {
  const loading = ref<boolean>(false);
  const vouchers = ref<Voucher[]>([]);
  const listVoucher = ref<Voucher[]>([]);
  const listAppliedCoupon = ref<AppliedCoupon[]>([]);
  const listVoucherInfo = ref<Voucher[]>([]);
  const clevertapStore = useClevertapStore();
  const { app } = useContext();
  const voucherBirthday = ref(null)

  // Helper function to track voucher events with CleverTap
  const trackVoucherEvent = (eventType: string, voucherCode: string | string[], errorMessage?: string) => {
    if (!process.client) return;
    
    try {
      const win = window as any;
      if (!win.clevertap) return;
      
      // Get cart/checkout ID from Magento
      const checkoutId = app.$vsf.$magento.config.state.getCartId();
      
      // Convert voucherCode to array if it's a string
      const codesArray = Array.isArray(voucherCode) ? voucherCode : [voucherCode];
      
      // Track event for each voucher code
      codesArray.forEach(code => {
        if (!code) return;
        
        // Define the tracking data type with optional error_message
        interface VoucherTrackingData {
          checkout_id: string;
          cart_id: string;
          voucher_code: string;
          error_message?: string;
        }
        
        // Prepare tracking data
        const trackingData: VoucherTrackingData = {
          checkout_id: checkoutId,
          cart_id: checkoutId,
          voucher_code: code
        };
        
        // Add error message for denied vouchers
        if (eventType === 'Voucher Denied' && errorMessage) {
          trackingData.error_message = errorMessage;
        }
        
        // Create clevertap data object to match pattern in clevertap.ts
        const clevertapData = {
          clevertap: win.clevertap,
          data: trackingData
        };
        
        // Send the appropriate event based on event type
        switch (eventType) {
          case 'Voucher Entered':
            clevertapStore.couponEnteredCleverTap(clevertapData);
            break;
          case 'Voucher Applied':
            clevertapStore.couponAppliedSuccessCleverTap(clevertapData);
            break;
          case 'Voucher Denied':
            clevertapStore.couponDeniedCleverTap(clevertapData);
            break;
          case 'Voucher Removed':
            clevertapStore.couponRemovedCleverTap(clevertapData);
            break;
        }
      });
    } catch (error) {
      console.error(`Error tracking ${eventType}:`, error);
    }
  };

  // Function to track coupon application from API response
  const trackCouponFromResponse = (response: any) => {
    if (!response) return;
    
    try {
      // Handle nested result structure
      const result = response.result?.result || response.result;
      
      if (Array.isArray(result)) {
        // Process each coupon in the result array
        result.forEach(couponResult => {
          if (couponResult.code) {
            if (couponResult.applied) {
              // Track successful application
              trackVoucherEvent('Voucher Applied', couponResult.code);
            } else {
              // Track denied application with message
              trackVoucherEvent('Voucher Denied', couponResult.code, couponResult.message || 'Voucher application failed');
            }
          }
        });
      }
    } catch (error) {
      console.error('Error processing coupon tracking:', error);
    }
  };

  async function load(paramUrl: string): Promise<void> {
    try {
      loading.value = true;
      const response = await getDataAxios(api.erpVoucher.list_coupon_information + paramUrl);
      if (response && response.data && response.data.length > 0) {
        const vouchersData = response.data.filter((e: Voucher) => e.is_active !== "0");
        vouchers.value = vouchersData;
      }
    } catch (error) {
      console.error("Error fetching vouchers:", error);
    } finally {
      loading.value = false;
    }
  }

  async function post(data: any): Promise<any> {
    try {
      loading.value = true;
      const response = await postDataAxios(api.erpVoucher.collect_voucher, data);
      return response;
    } catch (error) {
      console.error("Error fetching vouchers:", error);
      return null;
    } finally {
      loading.value = false;
    }
  }

  async function postListVoucher(param: ParamListVoucher): Promise<any> {
    try {
      loading.value = true;
      const response = await postDataAxios(api.customer.customer_getlistvoucher, param);
      if (response && response.vouchers && response.vouchers.length > 0) {
        listVoucher.value = response.vouchers as Voucher[];
      }
    } catch (error) {
      console.error("Error fetching vouchers:", error);
    } finally {
      loading.value = false;
    }
  }

  async function postMultiCoupon(param: ParamAppliedCoupon): Promise<any> {
    try {
      loading.value = true;
      
      // Track voucher entered event with either coupon_code or couponCodes
      if (param.couponCodes) {
        trackVoucherEvent('Voucher Entered', param.couponCodes);
      } else if (param.couponCodes && param.couponCodes.length > 0) {
        trackVoucherEvent('Voucher Entered', param.couponCodes);
      }
      
      const response = await postDataAxios(api.multiCoupon.multi_coupon_guest_apply, param);
      
      if (response && (response.result || response.code === 200)) {
        listAppliedCoupon.value = Array.isArray(response.result) ? 
          response.result as AppliedCoupon[] : 
          (response.result?.result as AppliedCoupon[] || []);
        
        // Track application using the new function with actual API response
        trackCouponFromResponse(response);
        
        return response;
      } else if (response && response.message) {
        // Track denied voucher with error message
        if (param.couponCodes) {
          trackVoucherEvent('Voucher Denied', param.couponCodes, response.message);
        } else if (param.couponCodes && param.couponCodes.length > 0) {
          trackVoucherEvent('Voucher Denied', param.couponCodes, response.message);
        }
        
        return response;
      }
    } catch (error) {
      console.error("Error applying voucher:", error);
      
      // Track denied voucher with generic error message
      if (param.couponCodes) {
        trackVoucherEvent('Voucher Denied', param.couponCodes, 'Unexpected error occurred');
      } else if (param.couponCodes && param.couponCodes.length > 0) {
        trackVoucherEvent('Voucher Denied', param.couponCodes, 'Unexpected error occurred');
      }
      
      return { success: false, message: 'Unexpected error occurred' };
    } finally {
      loading.value = false;
    }
  }

  async function postMultiCouponCustomer(param: ParamAppliedCoupon): Promise<any> {
    try {
      loading.value = true;
      
      // Track voucher entered event
      console.log("postMultiCouponCustomer", param);
      if (param.couponCodes) {
        trackVoucherEvent('Voucher Entered', param.couponCodes);
      } else if (param.couponCodes && param.couponCodes.length > 0) {
        trackVoucherEvent('Voucher Entered', param.couponCodes);
      }
      
      const response = await postDataAxios(api.multiCoupon.multi_coupon_me_apply, param);
      
      if (response && (response.result || response.code === 200)) {
        listAppliedCoupon.value = Array.isArray(response.result) ? 
          response.result as AppliedCoupon[] : 
          (response.result?.result as AppliedCoupon[] || []);
        
        // Track application using the new function with actual API response
        trackCouponFromResponse(response);
        
        return response;
      } else if (response && response.message) {
        // Track denied voucher with error message
        if (param.couponCodes) {
          trackVoucherEvent('Voucher Denied', param.couponCodes, response.message);
        } else if (param.couponCodes && param.couponCodes.length > 0) {
          trackVoucherEvent('Voucher Denied', param.couponCodes, response.message);
        }
        
        return response;
      }
    } catch (error) {
      console.error("Error applying voucher:", error);
      
      // Track denied voucher with generic error message
      if (param.couponCodes) {
        trackVoucherEvent('Voucher Denied', param.couponCodes, 'Unexpected error occurred');
      } else if (param.couponCodes && param.couponCodes.length > 0) {
        trackVoucherEvent('Voucher Denied', param.couponCodes, 'Unexpected error occurred');
      }
      
      return { success: false, message: 'Unexpected error occurred' };
    } finally {
      loading.value = false;
    }
  }

  async function postVoucherInfo(param: ParamVoucherInfo): Promise<any> {
    try {
      loading.value = true;
      const response = await postDataAxios(api.erpVoucher.voucher_infomation, param);
      if (response && response.data ) {
        listVoucherInfo.value = response.data as Voucher[];
      }
    } catch (error) {
      console.error("Error fetching vouchers:", error);
    } finally {
      loading.value = false;
    }
  }

  async function getVoucherInfo(param: ParamVoucherInfo): Promise<Voucher[]> {
    try {
      loading.value = true;
      const response = await postDataAxios(api.erpVoucher.voucher_infomation, param);
      return response?.data as Voucher[] || [];
    } catch (error) {
      console.error("Error fetching vouchers:", error);
    } finally {
      loading.value = false;
    }
  }

  async function postBirthdayLoyalty(useLoyalty:number): Promise<any> {
    try {
      loading.value = true;
      const cartId = app.$vsf.$magento.config.state.getCartId();
      const param = {
        cart_id: cartId,
        value: useLoyalty
      };
      const response = await postDataAxios(api.erp.use_loyalty, param);
      if (response && response.data) {
        voucherBirthday.value = response.data;
      }
    } catch (error) {
      console.error("Error fetching birthday loyalty:", error);
    } finally {
      loading.value = false;
    }
  }

  return {
    loading,
    vouchers,
    load,
    post,
    listVoucher,
    postListVoucher,
    postMultiCoupon,
    listAppliedCoupon,
    listVoucherInfo,
    postVoucherInfo,
    postMultiCouponCustomer,
    postBirthdayLoyalty,
    voucherBirthday,
    getVoucherInfo,
  };
}
